import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HelpCircle } from 'lucide-react';

import { fetchMtrSnMappings } from '#/api/imsMgmt/settings/mtr/fetchSnMappings';
import { replaceMtrSnMappings } from '#/api/imsMgmt/settings/mtr/replaceSnMappings';
import { PERM } from '#/config/permissions';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import { Checkbox } from '#/modules/commonComponents/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '#/modules/commonComponents/dialog/AlertDialogPrimitives';
import SnProductMappingStatusBadge
  from '#/modules/crmMgmt/aftersales/snProductMapping/components/SnProductMappingStatusBadge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '#/modules/commonComponents/tooltip';
import { useCan } from '#/modules/infra/auth/useCan';
import MtrSnMappingPickerDialog from '#/modules/imsMgmt/settings/mtr/components/MtrSnMappingPickerDialog';
import {
  getMtrApiErrorMessage,
  shouldReloadMtrSnMappings,
} from '#/modules/imsMgmt/settings/mtr/helper/apiError';
import {
  buildMtrSnMappingsPayload,
  mapMtrSnMappingRow,
  sortMtrSnMappings,
} from '#/modules/imsMgmt/settings/mtr/helper/snMappings';
import { mtrSnMappingsQueryKey } from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

function readUpdatedAt(data) {
  return data?.record?.updatedAt ?? null;
}

function readServerRows(data) {
  return sortMtrSnMappings((data?.snMappings ?? []).map(mapMtrSnMappingRow));
}

const MtrSnMappingsCard = ({ mtrId, detailUpdatedAt = null, onDetailRefetch }) => {
  const canUpdate = useCan(PERM.MTR_UPDATE);
  const queryClient = useQueryClient();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [pickerKey, setPickerKey] = useState(0);
  const [removedInactiveIds, setRemovedInactiveIds] = useState(() => new Set());
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [candidateError, setCandidateError] = useState('');
  const seenDetailUpdatedAtRef = useRef(detailUpdatedAt);

  const relationQuery = useQuery({
    queryKey: mtrSnMappingsQueryKey(mtrId),
    queryFn: () => fetchMtrSnMappings(mtrId),
    enabled: Boolean(mtrId),
  });

  const serverRows = useMemo(
    () => readServerRows(relationQuery.data?.data),
    [relationQuery.data],
  );
  const rows = serverRows;

  useEffect(() => {
    if (seenDetailUpdatedAtRef.current === detailUpdatedAt) {
      return;
    }
    seenDetailUpdatedAtRef.current = detailUpdatedAt;
    if (!mtrId) {
      return;
    }
    queryClient.invalidateQueries({ queryKey: mtrSnMappingsQueryKey(mtrId) });
  }, [detailUpdatedAt, mtrId, queryClient]);

  const saveMutation = useMutation({
    mutationFn: (nextRows) => replaceMtrSnMappings(
      mtrId,
      buildMtrSnMappingsPayload(nextRows ?? rows, readUpdatedAt(relationQuery.data?.data)),
    ),
    retry: false,
    onSuccess: async (result) => {
      toast.success(result?.message || '物料序號已更新');
      queryClient.setQueryData(mtrSnMappingsQueryKey(mtrId), result);
      setSelectedIds(new Set());
      setPickerOpen(false);
      setDeleteOpen(false);
      await onDetailRefetch?.();
    },
    onError: (error) => {
      toast.error(getMtrApiErrorMessage(error, '儲存物料序號失敗'));
      if (!shouldReloadMtrSnMappings(error)) {
        return;
      }
      setSelectedIds(new Set());
      setPickerOpen(false);
      setDeleteOpen(false);
      relationQuery.refetch();
      onDetailRefetch?.();
    },
  });

  function rememberRemovedInactive(previousRows, nextRows) {
    const nextIds = new Set(nextRows.map((row) => row.id));
    setRemovedInactiveIds((prev) => {
      const next = new Set(prev);
      previousRows.forEach((row) => {
        if (row.status === 'INACTIVE' && row.id && !nextIds.has(row.id)) {
          next.add(row.id);
        }
      });
      return next;
    });
  }

  function handleDelete() {
    if (selectedCount === 0 || isSaving) {
      return;
    }
    setDeleteOpen(true);
  }

  function handleCancelDelete() {
    if (isSaving) {
      return;
    }
    setDeleteOpen(false);
  }

  function handleConfirmDelete() {
    if (isSaving) {
      return;
    }
    const remaining = rows.filter((row) => !selectedIds.has(row.id));
    rememberRemovedInactive(rows, remaining);
    saveMutation.mutate(remaining);
  }

  function handleToggleAll(checked) {
    if (checked === true) {
      setSelectedIds(new Set(rows.map((row) => row.id)));
      return;
    }
    setSelectedIds(new Set());
  }

  function handleToggleRow(id, checked) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked === true) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }

  function handleConfirm(nextRows) {
    const mapped = sortMtrSnMappings(nextRows.map(mapMtrSnMappingRow));
    rememberRemovedInactive(rows, mapped);
    saveMutation.mutate(mapped);
  }

  function handleOpenPicker() {
    setPickerKey((prev) => prev + 1);
    setPickerOpen(true);
  }

  const isSaving = saveMutation.isPending;
  const selectedCount = rows.filter((row) => selectedIds.has(row.id)).length;
  const allSelected = rows.length > 0 && selectedCount === rows.length;
  let headerChecked = false;
  if (allSelected) {
    headerChecked = true;
  } else if (selectedCount > 0) {
    headerChecked = 'indeterminate';
  }
  const deleteDisabled = selectedCount === 0 || isSaving || relationQuery.isFetching;
  const deleteRows = sortMtrSnMappings(rows.filter((row) => selectedIds.has(row.id)));

  return (
    <div className="receipt-card-elevate">
      <div className="receipt-card-surface p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h2 className="form-block__title">物料序號</h2>
            <p className="mt-1 text-sm text-gray-600">
              確定綁定會立即寫入。要解除時先勾選，再按刪除確認。
            </p>
          </div>
          {canUpdate ? (
            <div className="flex shrink-0 flex-col items-end gap-2">
              <div className="flex flex-wrap items-center justify-end gap-2">
                <Button type="button" variant="outline" onClick={handleOpenPicker} disabled={isSaving}>
                  選擇物料序號
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deleteDisabled}
                >
                  刪除
                </Button>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className={cn(
                        'inline-flex cursor-help items-center border-0 bg-transparent p-0',
                        'text-gray-400 hover:text-primary-600',
                      )}
                      aria-label="刪除說明"
                    >
                      <HelpCircle size={16} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs">
                    先勾選要解除的物料序號，再按刪除。確認清單後會立即寫入。
                  </TooltipContent>
                </Tooltip>
              </div>
              {candidateError ? (
                <p className="text-sm text-destructive">{candidateError}</p>
              ) : null}
            </div>
          ) : null}
        </div>

        {relationQuery.isLoading ? (
          <p className="mt-4 text-sm text-gray-600">載入中…</p>
        ) : null}
        {relationQuery.isError ? (
          <p className="mt-4 text-sm text-destructive">
            {getMtrApiErrorMessage(relationQuery.error, '載入物料序號失敗')}
          </p>
        ) : null}

        {!relationQuery.isLoading && !relationQuery.isError && rows.length === 0 ? (
          <p className="mt-4 text-sm text-gray-600">尚未綁定物料序號</p>
        ) : null}

        {rows.length > 0 ? (
          <div className="mt-4 overflow-x-auto rounded border border-slate-200">
            <table className="w-full min-w-[24rem] text-left text-sm">
              <thead className="bg-slate-100 text-xs text-gray-600">
                <tr>
                  {canUpdate ? (
                    <th className="w-px px-2 py-1 text-center font-medium whitespace-nowrap">
                      <span className="inline-flex items-center gap-1">
                        <Checkbox
                          checked={headerChecked}
                          disabled={isSaving || rows.length === 0}
                          onCheckedChange={handleToggleAll}
                          aria-label="選取全部物料序號"
                        />
                        選取
                      </span>
                    </th>
                  ) : null}
                  <th className="w-px px-2 py-1 text-center font-medium whitespace-nowrap">狀態</th>
                  <th className="px-2 py-1 font-medium whitespace-nowrap">產品代碼</th>
                  <th className="px-2 py-1 font-medium whitespace-nowrap">產品名稱</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-t border-slate-200">
                    {canUpdate ? (
                      <td className="w-px px-2 py-1.5 text-center whitespace-nowrap">
                        <Checkbox
                          checked={selectedIds.has(row.id)}
                          disabled={isSaving}
                          onCheckedChange={(checked) => handleToggleRow(row.id, checked)}
                          aria-label={`選取 ${row.productCode || '物料序號'}`}
                        />
                      </td>
                    ) : null}
                    <td className="w-px px-2 py-1.5 text-center whitespace-nowrap">
                      <span className="inline-flex justify-center">
                        <SnProductMappingStatusBadge status={row.status} size="sm" />
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-gray-900">{row.productCode || '—'}</td>
                    <td className="px-2 py-1.5 text-gray-900">{row.productName || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      {pickerOpen ? (
        <MtrSnMappingPickerDialog
          key={pickerKey}
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          initialSelectedRows={rows}
          blockedIds={removedInactiveIds}
          onConfirm={handleConfirm}
          onListError={setCandidateError}
          isSaving={isSaving}
        />
      ) : null}
      <AlertDialog
        open={deleteOpen}
        onOpenChange={(nextOpen) => {
          if (isSaving) {
            return;
          }
          setDeleteOpen(nextOpen);
        }}
      >
        <AlertDialogContent
          className="gap-0 data-[size=default]:max-w-lg data-[size=default]:sm:max-w-lg"
          closeOnOutsideClick={false}
          closeOnEscape={!isSaving}
        >
          <AlertDialogHeader variant="destructive">
            <AlertDialogTitle>是否刪除物料序號</AlertDialogTitle>
            <AlertDialogDescription>確定後會立即解除以下關聯。</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="min-h-0 overflow-y-auto px-4 py-3">
            <div className="overflow-x-auto rounded border border-slate-200">
              <table className="w-full min-w-[24rem] text-left text-sm">
                <thead className="bg-slate-100 text-xs text-gray-600">
                  <tr>
                    <th className="w-px px-2 py-1 text-center font-medium whitespace-nowrap">狀態</th>
                    <th className="px-2 py-1 font-medium whitespace-nowrap">產品代碼</th>
                    <th className="px-2 py-1 font-medium whitespace-nowrap">產品名稱</th>
                  </tr>
                </thead>
                <tbody>
                  {deleteRows.map((row) => (
                    <tr key={row.id} className="border-t border-slate-200">
                      <td className="w-px px-2 py-1.5 text-center whitespace-nowrap">
                        <span className="inline-flex justify-center">
                          <SnProductMappingStatusBadge status={row.status} size="sm" />
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-gray-900">{row.productCode || '—'}</td>
                      <td className="px-2 py-1.5 text-gray-900">{row.productName || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <AlertDialogFooter divider="top" variant="destructive">
            <AlertDialogCancel
              onClick={(event) => {
                if (isSaving) {
                  event.preventDefault();
                  return;
                }
                handleCancelDelete();
              }}
            >
              取消
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={isSaving || deleteRows.length === 0}
              onClick={(event) => {
                event.preventDefault();
                handleConfirmDelete();
              }}
            >
              {isSaving ? '刪除中…' : '確定刪除'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

MtrSnMappingsCard.propTypes = {
  mtrId: PropTypes.string.isRequired,
  detailUpdatedAt: PropTypes.string,
  onDetailRefetch: PropTypes.func,
};

export default MtrSnMappingsCard;
